import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, Key, AlertTriangle } from 'lucide-react';

export default function SecuritySettings() {
  const { api } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(0);
  const [loading, setLoading] = useState(true);

  // Setup state
  const [setupPhase, setSetupPhase] = useState(null); // null | 'qr' | 'verify'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [setupRecoveryCodes, setSetupRecoveryCodes] = useState([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Disable state
  const [disableModal, setDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/auth/mfa/status');
        setMfaEnabled(res.data.mfa_enabled);
        setRecoveryCodes(res.data.recovery_codes_remaining);
      } catch {}
      setLoading(false);
    })();
  }, [api]);

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/mfa/setup');
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setSetupRecoveryCodes(res.data.recovery_codes);
      setSetupPhase('qr');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start MFA setup');
    }
  };

  const handleConfirm = async () => {
    if (verifyCode.length < 6) return;
    setConfirming(true);
    try {
      await api.post('/auth/mfa/confirm', { code: verifyCode });
      setMfaEnabled(true);
      setRecoveryCodes(setupRecoveryCodes.length);
      setSetupPhase(null);
      setVerifyCode('');
      toast.success('MFA enabled successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    }
    setConfirming(false);
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await api.post('/auth/mfa/disable', { password: disablePassword });
      setMfaEnabled(false);
      setRecoveryCodes(0);
      setDisableModal(false);
      setDisablePassword('');
      toast.success('MFA disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable MFA');
    }
    setDisabling(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied'));
  };

  return (
    <PlatformOwnerAppShell activeId="security" title="Security">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="security-settings-page">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Key className="w-5 h-5 text-[#004B8D]" />
                Two-Factor Authentication
              </CardTitle>
              {!loading && (
                <Badge className={mfaEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'} data-testid="mfa-status-badge">
                  {mfaEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Add an extra layer of security to your account using an authenticator app (Google Authenticator, Authy, etc.)
            </p>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-20 bg-slate-100 rounded-lg animate-pulse" />
            ) : mfaEnabled && !setupPhase ? (
              /* MFA is ON */
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <ShieldCheck className="w-6 h-6 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">MFA is active</p>
                    <p className="text-xs text-green-600">You'll be asked for a code each time you sign in. {recoveryCodes} recovery code{recoveryCodes !== 1 ? 's' : ''} remaining.</p>
                  </div>
                </div>
                <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDisableModal(true)} data-testid="disable-mfa-btn">
                  <ShieldOff className="w-4 h-4 mr-2" /> Disable MFA
                </Button>
              </div>
            ) : setupPhase === 'qr' ? (
              /* QR code + recovery codes display */
              <div className="space-y-5" data-testid="mfa-setup-qr">
                <div className="text-center">
                  <p className="text-sm font-medium mb-3">1. Scan this QR code with your authenticator app</p>
                  <img src={qrCode} alt="MFA QR Code" className="mx-auto rounded-lg border p-2" data-testid="mfa-qr-image" />
                  <div className="mt-2 flex items-center justify-center gap-1">
                    <p className="text-xs text-slate-400 font-mono">{secret}</p>
                    <button className="text-slate-400 hover:text-slate-600" onClick={() => copyToClipboard(secret)}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">2. Save these recovery codes somewhere safe</p>
                  <div className="bg-slate-50 border rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-1.5">
                      {setupRecoveryCodes.map((code, i) => (
                        <span key={i} className="font-mono text-xs text-slate-700 bg-white px-2 py-1 rounded border">{code}</span>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" className="mt-2 text-xs w-full" onClick={() => copyToClipboard(setupRecoveryCodes.join('\n'))} data-testid="copy-recovery-codes">
                      <Copy className="w-3 h-3 mr-1" /> Copy all codes
                    </Button>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg mt-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">These codes are only shown once. If you lose access to your authenticator app, you can use a recovery code to sign in.</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">3. Enter the 6-digit code from your app to confirm</p>
                  <div className="flex gap-2">
                    <Input
                      type="text" inputMode="numeric" maxLength={6}
                      placeholder="000000"
                      className="font-mono text-lg tracking-widest text-center flex-1"
                      value={verifyCode}
                      onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                      data-testid="mfa-verify-input"
                    />
                    <Button onClick={handleConfirm} disabled={confirming || verifyCode.length < 6} className="bg-[#004B8D] hover:bg-[#003a6d]" data-testid="mfa-confirm-btn">
                      {confirming ? 'Verifying...' : 'Confirm'}
                    </Button>
                  </div>
                </div>

                <Button variant="ghost" className="w-full text-sm text-slate-500" onClick={() => { setSetupPhase(null); setVerifyCode(''); }}>
                  Cancel setup
                </Button>
              </div>
            ) : (
              /* MFA is OFF */
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <ShieldOff className="w-6 h-6 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">MFA is not enabled</p>
                    <p className="text-xs text-slate-500">Enable two-factor authentication for enhanced account security.</p>
                  </div>
                </div>
                <Button onClick={handleSetup} className="bg-[#004B8D] hover:bg-[#003a6d]" data-testid="enable-mfa-btn">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Enable MFA
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disable MFA Modal */}
        {disableModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDisableModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4" data-testid="disable-mfa-modal">
              <h3 className="text-base font-semibold">Disable Two-Factor Authentication</h3>
              <p className="text-sm text-slate-500">Enter your password to confirm. This will remove MFA from your account.</p>
              <div className="space-y-1.5">
                <Label className="text-sm">Password</Label>
                <Input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Enter your password" data-testid="disable-mfa-password" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setDisableModal(false); setDisablePassword(''); }}>Cancel</Button>
                <Button variant="destructive" disabled={disabling || !disablePassword} onClick={handleDisable} data-testid="confirm-disable-mfa">
                  {disabling ? 'Disabling...' : 'Disable MFA'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PlatformOwnerAppShell>
  );
}
